import React, { createContext, useContext, useEffect, useState } from 'react';
import { getGroupData } from '../../../api/supabase/codeService';

const CHURCHSCHOOL_DEFAULT_PRICES = {
  wmw: {
    main: { original: 487000, discount: 244000 },
    sub: { original: 189000, discount: 109000 },
  },
  hfhj: {
    main: { original: 308000, discount: 174000 },
    sub: { original: 169000, discount: 109000 },
  },
};

export interface ChurchschoolPriceData {
  original: number;
  discount: number;
}

export interface ChurchschoolPrices {
  wmw: {
    main: ChurchschoolPriceData;
    sub: ChurchschoolPriceData;
  };
  hfhj: {
    main: ChurchschoolPriceData;
    sub: ChurchschoolPriceData;
  };
}

interface ChurchschoolPriceContextType {
  prices: ChurchschoolPrices | null;
  isLoading: boolean;
  error: string | null;
}

const ChurchschoolPriceContext = createContext<
  ChurchschoolPriceContextType | undefined
>(undefined);

// -----------------------------------------------------------------------------
//  캐싱 로직
//  여러 Provider 가 중첩되어도 Supabase 요청이 중복되지 않도록 모듈 레벨에
//  가격 데이터를 보관합니다.
// -----------------------------------------------------------------------------

let cachedPrices: ChurchschoolPrices | null = null;
let cachedError: string | null = null;
let cachedFetchPromise: Promise<void> | null = null;

export const ChurchschoolPriceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [prices, setPrices] = useState<ChurchschoolPrices | null>(
    CHURCHSCHOOL_DEFAULT_PRICES,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getPrice = (priceData: Record<string, string> | null, key: string) => {
    return Number(priceData?.[key] || 0);
  };

  useEffect(() => {
    // 이미 캐시가 존재하면 바로 사용하고 종료
    if (cachedPrices) {
      setPrices(cachedPrices);
      setIsLoading(false);
      setError(cachedError);
      return;
    }

    // fetch 가 이미 진행중이면 그 Promise 에 맞춰서 상태 업데이트만 수행
    if (cachedFetchPromise) {
      cachedFetchPromise.then(() => {
        setPrices(cachedPrices);
        setError(cachedError);
        setIsLoading(false);
      });
      return;
    }

    const fetchPrices = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await getGroupData('COURSE');

        const priceData: ChurchschoolPrices = {
          wmw: {
            main: {
              original: getPrice(res, 'CHURCHSCHOOL_WMW_MAIN_ORIGINAL'),
              discount: getPrice(res, 'CHURCHSCHOOL_WMW_MAIN_DISCOUNT'),
            },
            sub: {
              original: getPrice(res, 'CHURCHSCHOOL_WMW_SUB_ORIGINAL'),
              discount: getPrice(res, 'CHURCHSCHOOL_WMW_SUB_DISCOUNT'),
            },
          },
          hfhj: {
            main: {
              original: getPrice(res, 'CHURCHSCHOOL_HFHJ_MAIN_ORIGINAL'),
              discount: getPrice(res, 'CHURCHSCHOOL_HFHJ_MAIN_DISCOUNT'),
            },
            sub: {
              original: getPrice(res, 'CHURCHSCHOOL_HFHJ_SUB_ORIGINAL'),
              discount: getPrice(res, 'CHURCHSCHOOL_HFHJ_SUB_DISCOUNT'),
            },
          },
        };
        // 캐시에 저장
        cachedPrices = priceData;
        cachedError = null;

        setPrices(priceData);
      } catch (error) {
        console.error('COURSE 데이터 조회 실패:', error);
        cachedError = '가격 데이터를 불러오는데 실패했습니다.';
        setError(cachedError);
        setIsLoading(false);
      } finally {
        setIsLoading(false);
        cachedFetchPromise = null; // fetch 완료
      }
    };

    cachedFetchPromise = fetchPrices();
  }, []);

  return (
    <ChurchschoolPriceContext.Provider
      value={{
        prices: prices || CHURCHSCHOOL_DEFAULT_PRICES,
        isLoading,
        error,
      }}
    >
      {children}
    </ChurchschoolPriceContext.Provider>
  );
};

export const useChurchschoolPrices = () => {
  const context = useContext(ChurchschoolPriceContext);
  if (!context) {
    throw new Error(
      'useChurchschoolPrices must be used within a ChurchschoolPriceProvider',
    );
  }
  return context;
};
